import type {
  SingleIdParam,
  GatRouteWithParams,
  DefaultRoute,
  RoleIdParam,
  AgencySettingsIdParam
} from './router-types'

export const appRouteDefinitions = {
  root: 'root',
  auth: {
    loggedOut: 'logged-out',
    oAuth2Callback: 'auth-o-auth-callback',
    signIn: 'auth-sign-in',
    signOut: 'auth-sign-out'
  },
  dev: {
    httpClient: 'devHttpClient',
    signalR: 'devSignalR',
    styleGuide: 'devStyleGuide',
    accessControl: 'devAccessControl',
    typesGeneration: 'devTypesGeneration',
    components: 'devComponents',
    fredrik: 'devFredrik'
  }
} as const

type DevRoutes =
  (typeof appRouteDefinitions)['dev'][keyof (typeof appRouteDefinitions)['dev']]
type AuthRoutes =
  (typeof appRouteDefinitions)['auth'][keyof (typeof appRouteDefinitions)['auth']]

export const routeDefinitions = {
  search: {
    search: 'search'
  },
  mail: {
    mailInbox: 'mail-inbox',
    mailCompose: 'mail-compose'
  },
  mailIdRoutes: {
    mailMessage: 'mail-message'
  },
  portcall: {
    details: 'portcall-details',
    documents: 'portcall-documents'
  },
  dashboard: {
    ex1: 'dashboard-1'
  },
  user: {
    general: 'userSettings.general',
    applicationPreferences: 'userSettings.applicationPreferences',
    localization: 'userSettings.localization',
    featureFlags: 'userSettings.featureFlags'
  },
  stakeholders: {
    overview: 'stakeholders.overview',
    otherPartyCreate: 'stakeholders.other-party-create',
    other: {
      general: 'stakeholders.other-party-general',
      contacts: 'stakeholders.other-party-contacts',
      dangerZone: 'stakeholders.other-party-danger-zone'
    },
    clientCreate: 'stakeholders.client-create',
    client: {
      general: 'stakeholders.client-general',
      billing: 'stakeholders.client-billing',
      contacts: 'stakeholders.clients-contacts',
      followUps: 'stakeholders.clients-follow-ups',
      documents: 'stakeholders.client-documents',
      alerts: 'stakeholders.client-alerts',
      history: 'stakeholders.client-history',
      dangerZone: 'stakeholders.client-danger-zone'
    },
    vendorCreate: 'stakeholders.vendor-create',
    vendor: {
      general: 'stakeholders.vendor-general',
      billing: 'stakeholders.vendors-billing',
      contacts: 'stakeholders.vendors-contacts',
      followUps: 'stakeholders.vendors-follow-ups',
      documents: 'stakeholders.vendors-documents',
      alerts: 'stakeholders.vendors-alerts',
      history: 'stakeholders.vendors-history',
      dangerZone: 'stakeholders.vendors-danger-zone'
    }
  },
  support: {
    list: 'support.list',
    sessions: 'support.sessions',
    history: 'support.history'
  },
  notifications: {
    list: 'notification-list',
    settings: 'notification-settings'
  },
  administration: {
    agencyStructure: 'administration.agency-structure',
    companies: 'administration.companies',
    currencyCodes: 'administration.currency-codes',
    globalSettings: {
      base: 'administration.global-settings',
      monetary: 'administration.global-settings.monetary',
      invoicing: 'administration.global-settings.invoicing'
    },
    agencySettings: 'administration.agency-settings',
    accessControl: {
      accessRolesList: 'administration.access-roles-list',
      accessRole: {
        permissions: 'administration.access-role-permissions',
        dangerZone: 'administration.access-role-danger-zone'
      }
    },
    agency: {
      general: 'administration.agency-general',
      users: 'administration.agency-users',
      userTeams: 'administration.agency-user-teams',
      whiteboards: 'administration.agency-whiteboards',
      dangerZone: 'administration.agency-danger-zone'
    },
    tenant: {
      /** Tenant Users Route */
      tenantUsers: 'administration.tenant-users',
      /** Tenant User Details Route */
      tenantUser: {
        /** Tenant User General Route */
        general: 'administration.tenant-user-general',
        databaseAccess: 'administration.tenant-user-database-access',
        accessRoles: 'administration.tenant-user-access-roles',
        dangerZone: 'administration.tenant-user-danger-zone'
      }
    }
  }
} as const

type PortcallRoutes =
  (typeof routeDefinitions)['portcall'][keyof (typeof routeDefinitions)['portcall']]

type MailRoutes =
  (typeof routeDefinitions)['mail'][keyof (typeof routeDefinitions)['mail']]
type MailIdRoutes =
  (typeof routeDefinitions)['mailIdRoutes'][keyof (typeof routeDefinitions)['mailIdRoutes']]

type TenantUserRoutes =
  (typeof routeDefinitions)['administration']['tenant']['tenantUser'][keyof (typeof routeDefinitions)['administration']['tenant']['tenantUser']]

type AccessRoleRoutes =
  (typeof routeDefinitions)['administration']['accessControl']['accessRole'][keyof (typeof routeDefinitions)['administration']['accessControl']['accessRole']]

type DashboardRoutes =
  (typeof routeDefinitions)['dashboard'][keyof (typeof routeDefinitions)['dashboard']]

type UserRoutes =
  (typeof routeDefinitions)['user'][keyof (typeof routeDefinitions)['user']]

type StakeholderRoutes =
  | (typeof routeDefinitions)['stakeholders']['overview']
  | (typeof routeDefinitions)['stakeholders']['otherPartyCreate']
  | (typeof routeDefinitions)['stakeholders']['vendorCreate']
  | (typeof routeDefinitions)['stakeholders']['clientCreate']

type ClientRoutes =
  (typeof routeDefinitions)['stakeholders']['client'][keyof (typeof routeDefinitions)['stakeholders']['client']]

type VendorRoutes =
  (typeof routeDefinitions)['stakeholders']['vendor'][keyof (typeof routeDefinitions)['stakeholders']['vendor']]

type OtherPartyRoutes =
  (typeof routeDefinitions)['stakeholders']['other'][keyof (typeof routeDefinitions)['stakeholders']['other']]

type SupportRoutes =
  (typeof routeDefinitions)['support'][keyof (typeof routeDefinitions)['support']]

type NotificationsRoutes =
  (typeof routeDefinitions)['notifications'][keyof (typeof routeDefinitions)['notifications']]

/** No params here. */
type AdministrationRoutes =
  | (typeof routeDefinitions)['administration']['companies']
  | (typeof routeDefinitions)['administration']['agencyStructure']
  | (typeof routeDefinitions)['administration']['tenant']['tenantUsers']
  | (typeof routeDefinitions)['administration']['accessControl']['accessRolesList']
  | (typeof routeDefinitions)['administration']['currencyCodes']

export type AgencyRoutes =
  (typeof routeDefinitions)['administration']['agency'][keyof (typeof routeDefinitions)['administration']['agency']]

export type SearchRoutes =
  (typeof routeDefinitions)['search'][keyof (typeof routeDefinitions)['search']]

export type AgencySettingRoutes =
  (typeof routeDefinitions)['administration']['agencySettings']

export type AppRoute =
  | 'root'
  | AuthRoutes
  | DevRoutes
  | AdministrationRoutes
  | AgencyRoutes
  | UserRoutes
  | StakeholderRoutes
  | PortcallRoutes
  | DashboardRoutes
  | TenantUserRoutes
  | AccessRoleRoutes
  | SupportRoutes
  | NotificationsRoutes
  | MailRoutes
  | MailIdRoutes
  | OtherPartyRoutes
  | VendorRoutes
  | ClientRoutes
  | SearchRoutes
  | AgencySettingRoutes

// Add routes that require params:{id:string} here.
type RoutesWithSingleIdParams =
  | AgencyRoutes
  | PortcallRoutes
  | TenantUserRoutes
  | ClientRoutes
  | VendorRoutes
  | OtherPartyRoutes
  | MailIdRoutes

type RoutesWithRoleIdParams = AccessRoleRoutes

type RoutesWithAgencySettingsIdParams = AgencySettingRoutes

export type ParamRoutes<T> = T extends RoutesWithSingleIdParams
  ? GatRouteWithParams<T, SingleIdParam>
  : T extends RoutesWithRoleIdParams
    ? GatRouteWithParams<T, RoleIdParam>
    : T extends RoutesWithAgencySettingsIdParams
      ? GatRouteWithParams<T, AgencySettingsIdParam>
      : DefaultRoute<T>
